/* eslint-disable no-template-curly-in-string */
import React, { FC, useCallback } from "react";
import { colors } from "../../../../styles/variables";
import { CheckIconSvg, ErrorIconSvg } from "../../../icons";
import {
  StyledBold,
  StyledPasswordRequirement,
  StyledPasswordRequirements,
} from "./index.styled";
import { PortalPasswordLocalization } from "../../../../types/graphql/generated";
import { TemplateAsComponent } from "../../../TemplateAsComponent";

export enum PasswordRequirementsMsgKeys {
  min8CharactersRequired = "min8CharactersRequired",
  oneLowercaseAlphaRequired = "oneLowercaseAlphaRequired",
  oneSpecialCharRequired = "oneSpecialCharRequired",
  oneUppercaseAlphaRequired = "oneUppercaseAlphaRequired",
  twoNumericDigitsRequired = "twoNumericDigitsRequired",
}

// copied from v2 schema, once in v3 schema import that instead
export interface PasswordRequirement {
  msg: string;
  satisfied: boolean;
}

interface PasswordRequirementsProps {
  passwordRequirements: PasswordRequirement[];
  localization?: PortalPasswordLocalization | null;
}

export const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  passwordRequirements,
  localization,
}) => {
  const {
    minCharactersText = {
      bodyText: "At least ${boldText}",
      boldText: "8 characters",
    },
    oneUppercaseOneLowercaseAlphaText = {
      bodyText: "At least ${boldText1} & ${boldText2}",
      boldText1: "1 uppercase",
      boldText2: "1 lower case letter",
    },
    twoNumericDigitsText = {
      bodyText: "At least ${boldText}",
      boldText: "2 numbers",
    },
    oneSpecialCharText = {
      bodyText: "At least ${boldText} (e.g., !, $, @, %)",
      boldText: "1 special character",
    },
  } = localization || {};

  const Icon: FC<{ satisfied: boolean }> = ({ satisfied }) =>
    satisfied ? (
      <CheckIconSvg color={colors.green} width="18" height="18" />
    ) : (
      <ErrorIconSvg color={colors.gray300} width="18" height="18" />
    );

  const getRequirement = useCallback(
    (key: PasswordRequirementsMsgKeys) => {
      const req = passwordRequirements.find(({ msg }) => {
        const parsedMsg = msg as unknown as PasswordRequirementsMsgKeys;
        if (Object.values(PasswordRequirementsMsgKeys).includes(parsedMsg)) {
          return parsedMsg === key;
        }
        return null;
      });
      return req ? req.satisfied : false;
    },
    [passwordRequirements]
  );

  return (
    <StyledPasswordRequirements>
      <StyledPasswordRequirement>
        <Icon
          satisfied={getRequirement(
            PasswordRequirementsMsgKeys.min8CharactersRequired
          )}
        />
        <span>
          <TemplateAsComponent
            display="inline"
            template={minCharactersText.bodyText}
            map={{
              boldText: <StyledBold>{minCharactersText.boldText}</StyledBold>,
            }}
          />
        </span>
      </StyledPasswordRequirement>

      <StyledPasswordRequirement>
        <Icon
          satisfied={
            getRequirement(
              PasswordRequirementsMsgKeys.oneUppercaseAlphaRequired
            ) &&
            getRequirement(
              PasswordRequirementsMsgKeys.oneLowercaseAlphaRequired
            )
          }
        />
        <span>
          <TemplateAsComponent
            display="inline"
            template={oneUppercaseOneLowercaseAlphaText.bodyText}
            map={{
              boldText1: (
                <StyledBold>
                  {oneUppercaseOneLowercaseAlphaText.boldText1}
                </StyledBold>
              ),
              boldText2: (
                <StyledBold>
                  {oneUppercaseOneLowercaseAlphaText.boldText2}
                </StyledBold>
              ),
            }}
          />
        </span>
      </StyledPasswordRequirement>

      <StyledPasswordRequirement>
        <Icon
          satisfied={getRequirement(
            PasswordRequirementsMsgKeys.twoNumericDigitsRequired
          )}
        />
        <span>
          <TemplateAsComponent
            display="inline"
            template={twoNumericDigitsText.bodyText}
            map={{
              boldText: (
                <StyledBold>{twoNumericDigitsText.boldText}</StyledBold>
              ),
            }}
          />
        </span>
      </StyledPasswordRequirement>

      <StyledPasswordRequirement>
        <Icon
          satisfied={getRequirement(
            PasswordRequirementsMsgKeys.oneSpecialCharRequired
          )}
        />
        <span>
          <TemplateAsComponent
            display="inline"
            template={oneSpecialCharText.bodyText}
            map={{
              boldText: <StyledBold>{oneSpecialCharText.boldText}</StyledBold>,
            }}
          />
        </span>
      </StyledPasswordRequirement>
    </StyledPasswordRequirements>
  );
};
